import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Plus, 
  Search, 
  Download,
  Trash2,
  FileText,
  Calendar,
  User,
  Mail,
  Send,
  Clock
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PrescriptionsPage = () => {
  const { t } = useLanguage();
  const [prescriptions, setPrescriptions] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedPrescription, setSelectedPrescription] = useState(null);
  const [generating, setGenerating] = useState(false);
  
  const [formData, setFormData] = useState({
    template_id: '',
    client_id: '',
    prescription_date: new Date().toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [prescRes, templatesRes, clientsRes] = await Promise.all([
        axios.get(`${API}/prescriptions`),
        axios.get(`${API}/prescriptions/templates`),
        axios.get(`${API}/users/clients`)
      ]);
      setPrescriptions(prescRes.data);
      setTemplates(templatesRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    setGenerating(true);
    try {
      await axios.post(`${API}/prescriptions/generate`, formData);
      toast.success('Receta generada exitosamente');
      setShowCreateModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error generating prescription:', error);
      toast.error(error.response?.data?.detail || 'Error al generar receta');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async (prescription) => {
    try {
      const response = await axios.get(`${API}/prescriptions/${prescription.id}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const safeName = prescription.client_name.replace(/\s+/g, '_');
      link.setAttribute('download', `Receta_${safeName}_${prescription.prescription_date.replace(/\//g, '-')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF descargado');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Error al descargar PDF');
    }
  };

  const handleSendEmail = async (prescription) => {
    try {
      toast.loading('Enviando email...', { id: 'sending-email' });
      await axios.post(`${API}/prescriptions/${prescription.id}/send-email`);
      toast.success(`Email enviado a ${prescription.client_email}`, { id: 'sending-email' });
      fetchData();
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error(error.response?.data?.detail || 'Error al enviar email', { id: 'sending-email' });
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/prescriptions/${selectedPrescription.id}`);
      toast.success('Receta eliminada');
      setShowDeleteDialog(false);
      setSelectedPrescription(null);
      fetchData();
    } catch (error) {
      console.error('Error deleting prescription:', error);
      toast.error('Error al eliminar receta');
    }
  };

  const resetForm = () => {
    setFormData({
      template_id: '',
      client_id: '',
      prescription_date: new Date().toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })
    });
  };

  const filteredPrescriptions = prescriptions.filter(p =>
    p.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.template_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.prescription_date.includes(searchTerm)
  );

  const selectedClient = clients.find(c => c.id === formData.client_id);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#467322] border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="prescriptions-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold text-[#467322]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Recetas Médicas
          </h1>
          <p className="text-gray-600 mt-1">Generar y gestionar recetas médicas para clientes</p>
        </div>
        <Button 
          onClick={() => {
            resetForm();
            setShowCreateModal(true);
          }}
          className="bg-[#467322] hover:bg-[#3A601C] text-white"
          data-testid="create-prescription-btn"
        >
          <Plus size={20} className="mr-2" />
          Nueva Receta
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-white border border-[#467322]/20">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-[#467322]/10 rounded-lg flex items-center justify-center">
              <FileText className="text-[#467322]" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Recetas</p>
              <p className="text-2xl font-bold text-[#467322]">{prescriptions.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border border-[#467322]/20">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-[#467322]/10 rounded-lg flex items-center justify-center">
              <FileText className="text-[#467322]" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Plantillas Disponibles</p>
              <p className="text-2xl font-bold text-[#467322]">{templates.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border border-[#467322]/20">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-[#467322]/10 rounded-lg flex items-center justify-center">
              <Mail className="text-[#467322]" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Enviadas por Email</p>
              <p className="text-2xl font-bold text-[#467322]">{prescriptions.filter(p => p.emailed).length}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card className="bg-white border border-[#467322]/20">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#467322]" size={20} />
            <Input
              placeholder="Buscar por cliente, plantilla o fecha..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 border-[#467322]/30 focus:border-[#467322]"
              data-testid="search-prescriptions"
            />
          </div>
        </CardContent>
      </Card>

      {/* Prescriptions Table */}
      <Card className="bg-white border border-[#467322]/20">
        <CardHeader>
          <CardTitle className="text-[#467322] flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            <FileText size={24} />
            Historial de Recetas ({filteredPrescriptions.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredPrescriptions.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <FileText size={48} className="mx-auto mb-4 opacity-50" />
              <p>No hay recetas generadas</p>
              <p className="text-sm mt-1">Haz clic en "Nueva Receta" para crear una</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table className="table-striped">
                <TableHeader>
                  <TableRow className="bg-[#467322]">
                    <TableHead className="text-white font-semibold">Cliente</TableHead>
                    <TableHead className="text-white font-semibold">Plantilla</TableHead>
                    <TableHead className="text-white font-semibold">Fecha Receta</TableHead>
                    <TableHead className="text-white font-semibold">Generada</TableHead>
                    <TableHead className="text-white font-semibold">Email</TableHead>
                    <TableHead className="text-white font-semibold text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPrescriptions.map((prescription) => (
                    <TableRow key={prescription.id} className="hover:bg-[#F2F7F0]">
                      <TableCell className="font-medium">{prescription.client_name}</TableCell>
                      <TableCell className="text-sm">{prescription.template_name}</TableCell>
                      <TableCell>{prescription.prescription_date}</TableCell>
                      <TableCell className="text-sm">
                        {new Date(prescription.generated_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        {prescription.emailed ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                            <Mail size={12} /> Enviado
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-500 rounded-full text-xs font-medium">
                            Pendiente
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleSendEmail(prescription)}
                            className="text-blue-600 hover:bg-blue-50"
                            title="Enviar por email"
                            data-testid={`email-prescription-${prescription.id}`}
                          >
                            <Send size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownload(prescription)}
                            className="text-[#467322] hover:bg-[#F2F7F0]"
                            data-testid={`download-prescription-${prescription.id}`}
                          >
                            <Download size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedPrescription(prescription);
                              setShowDeleteDialog(true);
                            }}
                            className="text-red-500 hover:bg-red-50"
                            data-testid={`delete-prescription-${prescription.id}`}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-white border border-[#467322]/20 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl text-[#467322] text-center" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Nueva Receta Médica
            </DialogTitle>
            <DialogDescription className="text-center text-gray-500">
              Selecciona la plantilla, el cliente y la fecha
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleGenerate} className="space-y-6">
            {/* Template Selection */}
            <div className="space-y-2">
              <Label className="text-black font-medium">Tipo de Receta / Plantilla *</Label>
              <Select 
                value={formData.template_id} 
                onValueChange={(value) => setFormData({ ...formData, template_id: value })}
              >
                <SelectTrigger className="border-[#467322]/30 focus:border-[#467322] h-12" data-testid="select-template">
                  <SelectValue placeholder="Seleccionar plantilla" />
                </SelectTrigger>
                <SelectContent className="bg-white border border-[#467322]/20">
                  {templates.map((tmpl) => (
                    <SelectItem key={tmpl.id} value={tmpl.id}>
                      {tmpl.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formData.template_id && (
                <p className="text-xs text-gray-500">
                  {templates.find(t => t.id === formData.template_id)?.description}
                </p>
              )}
            </div>

            {/* Client Selection */}
            <div className="space-y-2">
              <Label className="text-black font-medium">Cliente *</Label>
              <Select 
                value={formData.client_id} 
                onValueChange={(value) => setFormData({ ...formData, client_id: value })}
              >
                <SelectTrigger className="border-[#467322]/30 focus:border-[#467322] h-12" data-testid="select-client">
                  <SelectValue placeholder="Seleccionar cliente" />
                </SelectTrigger>
                <SelectContent className="bg-white border border-[#467322]/20">
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name} ({client.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Selected Client Preview */}
            {selectedClient && (
              <div className="p-3 bg-[#F2F7F0] rounded-lg border border-[#467322]/10">
                <div className="flex items-center gap-2 mb-1">
                  <User className="text-[#467322]" size={16} />
                  <span className="font-medium text-black text-sm">{selectedClient.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="text-[#467322]" size={14} />
                  <span className="text-gray-600 text-xs">{selectedClient.email}</span>
                </div>
              </div>
            )}

            {/* Date */}
            <div className="space-y-2">
              <Label className="text-black font-medium">Fecha de la Receta *</Label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-[#467322]" size={18} />
                <Input
                  value={formData.prescription_date}
                  onChange={(e) => setFormData({ ...formData, prescription_date: e.target.value })}
                  placeholder="DD/MM/AAAA"
                  className="pl-10 border-[#467322]/30 focus:border-[#467322] h-12"
                  data-testid="input-prescription-date"
                  required
                />
              </div>
              <p className="text-xs text-gray-500">Formato: DD/MM/AAAA</p>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateModal(false)}
                className="flex-1 border-[#467322] text-[#467322]"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={!formData.template_id || !formData.client_id || !formData.prescription_date || generating}
                className="flex-1 bg-[#467322] hover:bg-[#3A601C] text-white"
                data-testid="generate-prescription-btn"
              >
                {generating ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    Generando...
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <FileText size={18} />
                    Generar Receta
                  </div>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="bg-white border border-[#467322]/20">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#467322]">{t('confirmDelete')}</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción eliminará la receta generada para {selectedPrescription?.client_name}. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#467322] text-[#467322]">
              {t('cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="confirm-delete-prescription"
            >
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PrescriptionsPage;
